import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

// Primitivas visuales propias del Wizard IG. Son a proposito locales: el
// diseño pide inputs en pildora y textareas muy redondeados, y el Input /
// Textarea compartidos de la app no deben cambiar para el resto de vistas.

const FIELD_BASE =
  'w-full border border-white/[0.08] bg-white/[0.04] text-[14.5px] text-foreground ' +
  'placeholder:text-white/35 transition-colors ' +
  'hover:border-white/[0.14] focus:border-primary/70 focus:bg-white/[0.06] ' +
  'focus:outline-none focus:ring-2 focus:ring-primary/25';

export function WizInput({ className = '', ...props }) {
  return <input {...props} className={`h-[50px] rounded-full px-5 ${FIELD_BASE} ${className}`} />;
}

export function WizTextarea({ value = '', maxLength = 500, className = '', ...props }) {
  return (
    <div className="relative">
      <textarea
        {...props}
        value={value}
        maxLength={maxLength}
        className={`resize-none rounded-[24px] px-5 pb-9 pt-4 leading-relaxed ${FIELD_BASE} ${className}`}
      />
      <span className="pointer-events-none absolute bottom-3 right-5 text-[11px] tabular-nums text-white/35">
        {value.length}/{maxLength}
      </span>
    </div>
  );
}

// Dropdown propio (no <select> nativo): el popup de un select del sistema
// no se puede stylear -- sale blanco y cuadrado sin importar las clases que
// le pongamos al elemento cerrado. Este arma el mismo contrato (value +
// onChange con target.value, <option> como children) pero dibuja su propio
// panel para que tambien el listado se vea como el resto del wizard.
export function WizSelect({ className = '', children, value, onChange, ...props }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const options = useMemo(
    () =>
      React.Children.toArray(children)
        .filter(React.isValidElement)
        .map((child) => ({
          value: child.props.value,
          label: child.props.children,
          disabled: !!child.props.disabled,
        })),
    [children]
  );

  const selected = options.find((o) => o.value === value && !o.disabled);

  useEffect(() => {
    if (!open) return undefined;
    function onDocPointer(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    function onKeyDown(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function pick(opt) {
    if (opt.disabled) return;
    onChange?.({ target: { value: opt.value } });
    setOpen(false);
  }

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        {...props}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`flex h-[50px] w-full items-center justify-between gap-2 rounded-full pl-5 pr-4 text-left ${FIELD_BASE} ${
          open ? 'border-primary/70 bg-white/[0.06] ring-2 ring-primary/25' : ''
        }`}
      >
        <span className={`truncate ${selected ? 'text-foreground' : 'text-white/35'}`}>
          {selected ? selected.label : options.find((o) => o.disabled)?.label}
        </span>
        <ChevronDown size={18} className={`shrink-0 text-white/45 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+8px)] z-30 max-h-64 overflow-auto rounded-[22px] border border-white/[0.1] bg-[#171126] p-1.5 shadow-[0_24px_60px_-14px_rgba(0,0,0,0.65),inset_0_1px_0_rgba(255,255,255,0.06)]"
        >
          {options
            .filter((opt) => !opt.disabled)
            .map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                role="option"
                aria-selected={opt.value === value}
                onClick={() => pick(opt)}
                className={`flex w-full items-center justify-between gap-2 rounded-full px-4 py-2.5 text-left text-[14px] transition-colors ${
                  opt.value === value
                    ? 'bg-primary/25 text-white'
                    : 'text-white/70 hover:bg-white/[0.07] hover:text-white'
                }`}
              >
                <span className="truncate">{opt.label}</span>
                {opt.value === value && <Check size={14} className="shrink-0 text-primary-foreground" />}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

export function WizSwitch({ checked, onCheckedChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onCheckedChange(!checked)}
      className={`relative h-[26px] w-[46px] shrink-0 rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 ${
        checked ? 'bg-primary shadow-[0_0_16px_rgba(124,58,237,0.55)]' : 'bg-white/15'
      }`}
    >
      <span
        className={`absolute left-[3px] top-[3px] h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

const BUTTON_VARIANTS = {
  // Todo es estatico salvo una capa de brillo que solo cambia de opacidad al
  // hover. Animar sombras grandes o transform con transition-all hacia que el
  // boton fuera a tirones.
  primary:
    'text-white bg-[linear-gradient(180deg,#9a7bff_0%,#7c4dff_48%,#6a35f0_100%)] ' +
    'shadow-[0_10px_26px_-8px_rgba(124,77,255,0.7),inset_0_1px_0_rgba(255,255,255,0.35),inset_0_0_0_1px_rgba(255,255,255,0.12)] ' +
    'disabled:opacity-45 disabled:shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]',
  ghost:
    'text-foreground bg-white/[0.04] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] disabled:opacity-40',
};

export function WizButton({ variant = 'primary', className = '', children, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`group relative inline-flex h-[52px] shrink-0 items-center justify-center overflow-hidden rounded-full px-8 text-[15px] font-medium leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 disabled:pointer-events-none ${BUTTON_VARIANTS[variant]} ${className}`}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full bg-white/[0.14] opacity-0 transition-opacity duration-150 group-hover:opacity-100"
      />
      <span className="relative flex items-center justify-center gap-2.5">{children}</span>
    </button>
  );
}

export function WizField({ icon, label, children, className = '' }) {
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <label className="flex items-center gap-2 text-[13px] font-medium text-white/60">
        {icon && <span className="text-white/45">{icon}</span>}
        {label}
      </label>
      {children}
    </div>
  );
}

export function StepHeader({ eyebrow, title, sub }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-[11.5px] font-medium uppercase tracking-[0.12em] text-white/40">{eyebrow}</div>
      <h3 className="text-[26px] font-medium leading-tight tracking-tight lg:text-[34px]">{title}</h3>
      {sub && <p className="max-w-[52ch] text-[14px] leading-relaxed text-white/50 lg:text-[15.5px]">{sub}</p>}
    </div>
  );
}
